// Infinite scroll for the collection product grid.
// The element lives inside #ProductGridContainer, so it is re-created (and
// re-initialized) whenever facets.js replaces the grid after filtering/sorting.
// Each batch is fetched by rendering the section for the next page
// (?section_id=...&page=N), then the returned <li> items are appended.
class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.nextUrl = this.dataset.nextUrl || null;
    this.observer = null;
  }

  connectedCallback() {
    if (!this.nextUrl) {
      // Single page collection, nothing to load.
      this.classList.add('is-complete');
      return;
    }
    this.initObserver();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  initObserver() {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.loadNextPage();
        });
      },
      // Start fetching shortly before the end of the grid enters the viewport.
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this);
  }

  loadNextPage() {
    if (this.isLoading || !this.nextUrl) return;
    this.isLoading = true;
    this.classList.add('is-loading');

    const requestUrl = new URL(this.nextUrl, window.location.origin);
    requestUrl.searchParams.set('section_id', this.dataset.sectionId);

    fetch(requestUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load the next page (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        // The element was removed while fetching (e.g. filters re-rendered
        // the grid) — drop the result, the new instance starts fresh.
        if (!this.isConnected) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.appendProducts(html.getElementById('product-grid'));

        const nextElement = html.querySelector('infinite-scroll');
        this.nextUrl = nextElement && nextElement.dataset.nextUrl ? nextElement.dataset.nextUrl : null;
      })
      .catch((error) => {
        // Keep nextUrl and leave the observer armed so scrolling away and
        // back retries instead of breaking the page.
        console.error('Infinite scroll:', error);
      })
      .finally(() => {
        this.isLoading = false;
        this.classList.remove('is-loading');

        if (!this.nextUrl) {
          this.classList.add('is-complete');
          if (this.observer) this.observer.disconnect();
          return;
        }

        // Re-arm the observer: after appending, the element may still be in
        // view (short pages / tall viewports) and no new intersection event
        // would fire, which would stall the loading chain.
        if (this.observer) {
          this.observer.disconnect();
          this.observer.observe(this);
        }
      });
  }

  appendProducts(fetchedGrid) {
    const grid = document.getElementById('product-grid');
    if (!fetchedGrid || !grid) return;

    // Deduplicate by product id (anchor ids end with "-<product_id>").
    // Card hrefs can't be used as keys: this theme's card template renders a
    // hidden "StandardCardNoMediaLink" anchor whose href is the collection
    // URL itself (product.url is nil inside the snippet), identical on every
    // card, which would make every fetched card look like a duplicate.
    const cardKey = (item) => {
      const link = item.querySelector('a[id^="CardLink-"], a[id^="StandardCardNoMediaLink-"]');
      if (link) {
        const match = link.id.match(/-(\d+)$/);
        if (match) return 'id:' + match[1];
      }
      const productLink = item.querySelector('.card__heading a.full-unstyled-link[href*="/products/"]');
      return productLink ? 'url:' + productLink.getAttribute('href') : null;
    };

    const existingKeys = new Set(
      Array.from(grid.querySelectorAll(':scope > li.grid__item'))
        .map((item) => cardKey(item))
        .filter(Boolean)
    );

    const fragment = document.createDocumentFragment();
    const appendedItems = [];
    fetchedGrid.querySelectorAll(':scope > li.grid__item').forEach((item) => {
      const key = cardKey(item);
      // Skip products already on screen (e.g. the pinned products that page 1
      // renders on top of the grid).
      if (key && existingKeys.has(key)) return;
      if (key) existingKeys.add(key);
      appendedItems.push(item);
      fragment.appendChild(document.importNode(item, true));
    });
    grid.appendChild(fragment);

    // Scroll-in reveal for the new cards, mirroring animations.js behaviour
    // (that helper can't be reused here: it only accepts an element root and
    // the trigger class sits on the <li> itself).
    this.revealItems(appendedItems);

    // Re-initialize per-card behaviours for the appended items.
    if (typeof initProductColorSwatches === 'function') initProductColorSwatches(grid);
  }

  revealItems(items) {
    if (items.length === 0 || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('scroll-trigger--offscreen');
            observer.unobserve(entry.target);
          } else {
            entry.target.classList.add('scroll-trigger--offscreen');
          }
        });
      },
      { rootMargin: '0px 0px -50px 0px' }
    );
    items.forEach((item) => observer.observe(item));
  }
}

customElements.define('infinite-scroll', InfiniteScroll);
